import { useMutation, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import {
  FilterFileResolutionRequest,
  InternalDashboardTableResponseDtoGettingFileResolutionDto,
  InternalDashboardTableResponseDtoInvoiceFileProcessingDto,
  InvoiceMessageDto,
  InvoiceOcrMessageDto,
  JsonNode,
  SearchingInvoiceFileProcessingRequest,
  UpdatingResolutionStatusRequest,
  UpdatingS3FileMetadataReq,
} from 'src/generated/services/TFinancialApi';
import { tFinancialService } from 'src/infra/services/tFinancialService';
import { IInternalDashboardFilterParams } from 'src/presentations/components/organisms/internalDashboard/types';
import { CacheKeys, cacheKeysUtil } from 'src/utils/cacheKeysUtil';

export const useGetActionNeeded = (
  params: IInternalDashboardFilterParams,
  enabled?: boolean,
  intervalTime: number = 60000
): UseQueryResult<InternalDashboardTableResponseDtoGettingFileResolutionDto> => {
  const { page, tabName, paramsIsNull, fileResolutionStatuses, ...others } = params;
  const newParams = {
    ...others,
    page: page - 1,
    isUnresolvedStatus: true,
    fileResolutionStatuses: !paramsIsNull ? fileResolutionStatuses : null,
  };

  return useQuery(
    cacheKeysUtil.getInternalActionNeeded(params),
    async (): Promise<InternalDashboardTableResponseDtoGettingFileResolutionDto> => (await tFinancialService.api.getFileResolutions(newParams)).data.data,
    {
      refetchInterval: intervalTime,
      enabled,
    }
  );
};

export const useGetIssues = (
  params: IInternalDashboardFilterParams,
  enabled?: boolean,
  intervalTime: number = 60000
): UseQueryResult<InternalDashboardTableResponseDtoGettingFileResolutionDto> => {
  const { page, tabName, paramsIsNull, fileResolutionStatuses, ...others } = params;
  const newParams = {
    ...others,
    page: page - 1,
    isUnresolvedStatus: false,
    fileResolutionStatuses: !paramsIsNull ? fileResolutionStatuses : null,
  };

  return useQuery(
    cacheKeysUtil.getInternalIssues(params),
    async (): Promise<InternalDashboardTableResponseDtoGettingFileResolutionDto> => (await tFinancialService.api.getFileResolutions(newParams)).data.data,
    {
      refetchInterval: intervalTime,
      enabled,
    }
  );
};

export const useGetUserIssues = (
  params: IInternalDashboardFilterParams,
  enabled?: boolean,
  intervalTime: number = 60000
): UseQueryResult<InternalDashboardTableResponseDtoGettingFileResolutionDto> => {
  const { page, tabName, paramsIsNull, fileResolutionStatuses, ...others } = params;
  const newParams = {
    ...others,
    page: page - 1,
    isUnresolvedStatus: tabName === 'ACTION_NEEDED',
    fileResolutionStatuses: !paramsIsNull ? fileResolutionStatuses : null,
  };

  return useQuery(
    cacheKeysUtil.getInternalIssues(params),
    async (): Promise<InternalDashboardTableResponseDtoGettingFileResolutionDto> => (await tFinancialService.api.getFileResolutions(newParams)).data.data,
    {
      refetchInterval: intervalTime,
      enabled,
    }
  );
};

export const useGetFiles = (
  params: IInternalDashboardFilterParams,
  enabled?: boolean,
  intervalTime: number = 60000
): UseQueryResult<InternalDashboardTableResponseDtoInvoiceFileProcessingDto> => {
  const { page, tabName, paramsIsNull, fileResolutionStatuses, ...others } = params;
  const newParams = {
    ...others,
    page: page - 1,
    fileResolutionStatuses: !paramsIsNull ? fileResolutionStatuses : null,
  };
  return useQuery(
    cacheKeysUtil.getInternalFiles(params),
    async (): Promise<InternalDashboardTableResponseDtoInvoiceFileProcessingDto> => (await tFinancialService.api.query(newParams)).data.data,
    {
      refetchInterval: intervalTime,
      enabled,
      cacheTime: 0,
      keepPreviousData: false,
    }
  );
};

export const useGetFileUrl = (invoiceFileProcessingId: string): UseQueryResult<string> =>
  useQuery(
    cacheKeysUtil.getFileProcessingUrl(invoiceFileProcessingId),
    async (): Promise<string> => (await tFinancialService.api.getFileUrl(invoiceFileProcessingId)).data.data,
    {
      enabled: false,
    }
  );

export const useGetInvoiceConfigId = (): UseQueryResult<string[]> =>
  useQuery(cacheKeysUtil.getInvoiceConfigId(), async (): Promise<string[]> => (await tFinancialService.api.getAllInvoiceDataConfigId()).data.data);

export const useGetInvoiceData = (invoiceFileProcessingId: string, enabled?: boolean): UseQueryResult<InvoiceOcrMessageDto> =>
  useQuery(
    cacheKeysUtil.getInvoiceData(invoiceFileProcessingId),
    async (): Promise<InvoiceOcrMessageDto> => (await tFinancialService.api.getOcrMessageByInvoiceFileProcessingId(invoiceFileProcessingId)).data.data,
    {
      enabled: !!invoiceFileProcessingId && enabled,
    }
  );

export const useGetMetadataOfFile = (invoiceFileProcessingId: string): UseQueryResult<Record<string, string>> =>
  useQuery(
    cacheKeysUtil.getMetadataOfFile(invoiceFileProcessingId),
    async (): Promise<Record<string, string>> => (await tFinancialService.api.getMetadata(invoiceFileProcessingId)).data.data,
    {
      enabled: !!invoiceFileProcessingId,
    }
  );

interface UpdateMetadataRequestDto {
  data: UpdatingS3FileMetadataReq;
  invoiceFileProcessingId?: string;
  params: FilterFileResolutionRequest | SearchingInvoiceFileProcessingRequest;
}

export const useUpdateMetadataOfFile = () => {
  const queryClient = useQueryClient();

  return useMutation(async (data: UpdateMetadataRequestDto) => tFinancialService.api.updateMetadataOfFile(data.data), {
    onSuccess: async (_, args) => {
      await Promise.all([
        queryClient.invalidateQueries({
          exact: false,
          queryKey: [CacheKeys.getInternalIssues],
        }),
        queryClient.invalidateQueries({
          exact: false,
          queryKey: [CacheKeys.getInternalActionNeeded],
        }),
        queryClient.invalidateQueries({
          exact: false,
          queryKey: [CacheKeys.getInternalFiles],
        }),
        queryClient.invalidateQueries(cacheKeysUtil.getMetadataOfFile(args?.invoiceFileProcessingId)),
      ]);
    },
  });
};

interface ResolveRequestDto {
  id: number;
  data: UpdatingResolutionStatusRequest;
  params: FilterFileResolutionRequest | SearchingInvoiceFileProcessingRequest;
}

export const useResolveFileResolution = () => {
  const queryClient = useQueryClient();

  return useMutation(async (data: ResolveRequestDto) => tFinancialService.api.updateStatus(data.id, data.data), {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onSuccess: async (_, args) => {
      await Promise.all([
        queryClient.invalidateQueries({
          exact: false,
          queryKey: [CacheKeys.getInternalIssues],
        }),
        queryClient.invalidateQueries({
          exact: false,
          queryKey: [CacheKeys.getInternalActionNeeded],
        }),
        queryClient.invalidateQueries({
          exact: false,
          queryKey: [CacheKeys.getInternalFiles],
        }),
      ]);
    },
  });
};
interface UserResolveRequestDto {
  id: number;
  data: UpdatingResolutionStatusRequest;
  params?: FilterFileResolutionRequest | SearchingInvoiceFileProcessingRequest;
}
export const useResolveUserResolution = () => {
  const queryClient = useQueryClient();
  return useMutation(async (data: UserResolveRequestDto) => tFinancialService.api.updateStatus(data.id, data.data), {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onSuccess: async (_, args) => {
      await Promise.all([
        queryClient.invalidateQueries({
          exact: false,
          queryKey: [CacheKeys.getInternalIssues],
        }),
        queryClient.invalidateQueries({
          exact: false,
          queryKey: [CacheKeys.getInternalActionNeeded],
        }),
        queryClient.invalidateQueries({
          exact: false,
          queryKey: [CacheKeys.getInternalFiles],
        }),
      ]);
    },
  });
};

interface UpdateOcrDataRequestDto {
  invoiceFileProcessingId: string;
  data: InvoiceMessageDto;
  params: FilterFileResolutionRequest | SearchingInvoiceFileProcessingRequest;
}

export const useUpdateInvoiceData = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (data: UpdateOcrDataRequestDto) => {
      if (data.data.pipelineId !== data.invoiceFileProcessingId) {
        throw new Error('Pipeline ID does not match Invoice File Processing ID');
      }
      await tFinancialService.api.reImport(data.data);
    },
    {
      onSuccess: async (_, args) => {
        await Promise.all([
          queryClient.invalidateQueries({
            exact: false,
            queryKey: [CacheKeys.getInternalIssues],
          }),
          queryClient.invalidateQueries({
            exact: false,
            queryKey: [CacheKeys.getInternalActionNeeded],
          }),
          queryClient.invalidateQueries({
            exact: false,
            queryKey: [CacheKeys.getInternalFiles],
          }),
          queryClient.invalidateQueries({
            exact: false,
            queryKey: cacheKeysUtil.getInvoiceData(args.invoiceFileProcessingId),
          }),
        ]);
      },
    }
  );
};

export const useGetOcrData = (invoiceFileProcessingId: string, enabled?: boolean): UseQueryResult<JsonNode> =>
  useQuery(
    cacheKeysUtil.getOcrData(invoiceFileProcessingId),
    async (): Promise<JsonNode> => (await tFinancialService.api.getOcrOutput(invoiceFileProcessingId)).data.data,
    {
      enabled: !!invoiceFileProcessingId && enabled,
    }
  );

export const useUpdateOcrData = (invoiceFileProcessingId: string) => {
  const queryClient = useQueryClient();

  return useMutation(
    async (data: JsonNode) => {
      await tFinancialService.api.updateOcrOutput(invoiceFileProcessingId, data);
    },
    {
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries(cacheKeysUtil.getOcrData(invoiceFileProcessingId)),
          queryClient.invalidateQueries({
            exact: false,
            queryKey: [CacheKeys.getInternalIssues],
          }),
          queryClient.invalidateQueries({
            exact: false,
            queryKey: [CacheKeys.getInternalActionNeeded],
          }),
          queryClient.invalidateQueries({
            exact: false,
            queryKey: [CacheKeys.getInternalFiles],
          }),
        ]);
      },
    }
  );
};
